import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import { combineLatest, debounce, Observable, timer } from 'rxjs';
import { ReorderDetails, ReorderDetailsItem } from 'src/app/core/models';
import { EnvService } from 'src/app/core/services';
import { SetProductItemQuantitiesUserData } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.actions';
import { OrderWizardDataState } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import { ProductItemQuantityUserData } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.model';
import { AbstractControlWithWarning, itemQuantityValidator } from 'src/app/core/utils/validators';

@Component({
  selector: 'app-product-items',
  templateUrl: './product-items.component.html',
  styleUrls: ['./product-items.component.scss'],
})
export class ProductItemsComponent implements OnInit {
  @Select(OrderWizardDataState.reorder) reorder$!: Observable<ReorderDetails>;
  @Select(OrderWizardDataState.productItemQuantitiesUserData) productItemQuantitiesUserData$!: Observable<
    ProductItemQuantityUserData[] | null
  >;

  initialQuantities: (number | undefined)[] = [];
  items: ReorderDetailsItem[] = [];

  itemsForm?: FormArray;

  constructor(private store: Store, private formBuilder: FormBuilder, private envService: EnvService) {}

  ngOnInit() {
    this.initiateForm();
  }

  initiateForm() {
    combineLatest([this.reorder$, this.productItemQuantitiesUserData$]).subscribe(([reorder, quantitiesUserData]) => {
      if (!reorder) return;

      this.items = reorder.items;
      const updatedInitialQuantities = reorder.items.map((item) => item.quantity);

      // TODO Remove this line after updating the store
      // This was added to prevent against redundant re-evaluations of reorder$
      if (isEqual(this.initialQuantities, updatedInitialQuantities)) return;

      this.initialQuantities = updatedInitialQuantities;

      if (!quantitiesUserData) {
        this.itemsForm = new FormArray(
          reorder.items.map((item, i) => {
            const quantityControl = new FormControl(this.initialQuantities[i], [
              itemQuantityValidator(item, this.envService.ccs),
            ]);
            if (item.isNoCharge) quantityControl.disable();

            return new FormGroup({
              itemUiId: new FormControl(item.uiId),
              quantity: quantityControl,
            });
          })
        );
      } else if (!this.itemsForm) {
        this.itemsForm = new FormArray(
          quantitiesUserData.map((qud, i) => {
            const item = this.items[i];
            const quantityControl = new FormControl(qud.userQuantity, [
              itemQuantityValidator(reorder.items[i], this.envService.ccs),
            ]);
            if (item.isNoCharge) quantityControl.disable();

            return new FormGroup({
              itemUiId: new FormControl(qud.itemUiId),
              quantity: quantityControl,
            });
          })
        );
      }

      this.setFormListener();
    });
  }

  setFormListener() {
    if (!this.itemsForm) return;

    this.itemsForm.valueChanges.pipe(debounce(() => timer(100))).subscribe((values) => {
      if (!this.itemsForm) return;

      const quantitiesUserData: ProductItemQuantityUserData[] = this.itemsForm.controls.map(
        (c: AbstractControl, i: number) => {
          const initialQuantity = this.initialQuantities[i]!;
          const formGroup = c as FormGroup;
          const itemUiId = formGroup.controls['itemUiId'].value;
          const userQuantity = formGroup.controls['quantity'].value;
          const maxQuantity = this.items[i].maxValidShippingQuantity;
          const invalidValueWarning = (formGroup.controls['quantity'] as AbstractControlWithWarning).warnings
            ?.invalidValue;

          return {
            itemUiId,
            initialQuantity,
            userQuantity,
            quantityToSubmit: invalidValueWarning ? invalidValueWarning.quantityToSubmit : userQuantity,
            maxQuantity: maxQuantity,
            warningTitle: invalidValueWarning?.title || null,
            warningMessage: invalidValueWarning?.message || null,
          };
        }
      );

      this.store.dispatch(new SetProductItemQuantitiesUserData(quantitiesUserData));
    });
  }

  onRemoveItem(index: number) {
    this.itemsForm?.at(index).get('quantity')?.setValue(0);
  }

  onResetItem(index: number) {
    this.itemsForm?.at(index).get('quantity')?.setValue(this.initialQuantities[index]);
  }

  asFormControl(a: AbstractControl) {
    return a as FormControl;
  }

  asWarningControl(a: AbstractControl) {
    return a as AbstractControlWithWarning;
  }
}
